import { Injectable } from '@angular/core';

import { ESRI } from '../esri-loader-util';
import { IMapcoreFeatureLayer, IMapcoreMapImageLayer, MapCoreLayer } from '../types';

@Injectable({
    providedIn: 'root'
})
export class LegendService {
    private readonly hardcodedLayerId = 4;

    public getDefaultColors(): Array<Array<number>> {
        const max = 255;
        const zero = 0;
        const fifth = 53;
        const third = 75;
        const overHundred = 117;
        const half = 153;
        const twoThrid = 190;
        const dim = 11;
        const defaultColors: Array<Array<number>> = [
            [max, zero, zero],
            [zero, max, zero],
            [zero, zero, max],
            [max, half, zero],
            [half, zero, max],
            [zero, max, max],
            [max, zero, max],
            [max, max, zero],
            [half, zero, zero],
            [fifth, overHundred, dim],
            [dim, third, half],
            [twoThrid, third, dim],
            [fifth, dim, overHundred],
            [dim, third, third],
            [overHundred, dim, third],
            [twoThrid, half, zero]
        ];
        return defaultColors;
    }

    public async getLayerLegend(layer: __esri.FeatureLayer) {

        let url, layerId;
        if (layer !== null && layer !== undefined) {
            url = layer.url + '/legend?f=json';
            layerId = layer.layerId;
        } else {
            // TODO: hardcode for now until map layers can be avaialbe from  map
            url = 'https://www.dotdev7.state.pa.us/penndotgis/rest/services/GISUSER/GISUSER/MapServer/legend?f=json';
            layerId = this.hardcodedLayerId;
        }

        return await this.getLegendByLayerId(url, layerId);
    }

    public async getLayerLegendByLayerId(layerId: number) {

        const url = 'https://www.dotdev7.state.pa.us/penndotgis/rest/services/GISUSER/GISUSER/MapServer/legend?f=json';

        return await this.getLegendByLayerId(url, layerId);
    }

    public async esriConfig() {
        const config = await ESRI.Config;
        this.getCorsEnabledHosts().forEach(host => {
            (<any>config.request).corsEnabledServers.push(host); // To be deprecated: http://esriurl.com/cors8664
            // Replace with 'trustedServers' property on request:
            // https://developers.arcgis.com/javascript/latest/api-reference/esri-config.html#request
        });
    }

    public async getEsriColor(color) {

        const [Color] = await ESRI.loadModules('esri/Color');

        if (!color) {
            return null;
        } else if (color.declaredClass === 'esri.Color') {
            return color;
        } else if (typeof color === 'string') {
            if (color.indexOf('rgb') === 0) {
                return __esri.Color.fromRgb(color);
            } else {
                return __esri.Color.fromString(color);
            }
        } else if (Array.isArray(color)) {
            return new Color(color);
        } else if (color.r !== undefined && color.g !== undefined && color.b !== undefined) {
            return new Color([color.r, color.g, color.b, color.a]);
        } else {
            return new Color();
        }
    }

    private async getLegendByLayerId(serviceUrl: string, layerId: number) {

        await this.esriConfig();

        const esriRequest = (await ESRI.Request) as any;

        const resp = await esriRequest(serviceUrl, {
            responseType: 'json'
        });

        const legendLyr = resp.data.layers.find(lyr => lyr.layerId === layerId);
        if (legendLyr && legendLyr.legend[0]) {
            const legend = legendLyr.legend[0];
            if (legend.imageData.length > 0) {
                return `data:${legend.contentType};base64,${legend.imageData}`;
            } else {
                return '';
            }
        }
        return '';
    }

    public getNewColor(map: __esri.Map, skip?: boolean) {
        if (skip) {
            return [0, 0, 0];
        }
        const existingColors = this.getExistingLayerColors(map);
        const defaultColors = this.getDefaultColors();
        const newColor = defaultColors.find(([r, g, b]) => {
            return !existingColors.find(([x, y, z]) => r === x && g === y && b === z)
                && !this.promisedColors.find(([x, y, z]) => r === x && g === y && b === z);
        }) || this.getRandomColorRGB();
        this.promisedColors.push(newColor);
        this.promisedColorsTimeout = setTimeout(() => this.promisedColors = [], 1000);
        return newColor;
    }

    promisedColors: number[][] = [];
    promisedColorsTimeout;

    getRandomColor() {
        const hexCombinations = 16777216;
        const radix = 16;
        return '#' + Math.floor(Math.random() * hexCombinations).toString(radix);
    }

    public getRandomColorRGB() {
        const maxRgb = 256;
        return [Math.floor(Math.random() * maxRgb), Math.floor(Math.random() * maxRgb), Math.floor(Math.random() * maxRgb)];
    }

    // Formats old simple renderers properly
    getModifiedRenderer(renderer: __esri.SimpleRenderer) {
        if (renderer.symbol) {
            this.convertSymbolAlphaColorToZeroToOneBased(renderer.symbol); // Older definitons may still need this prior to creation
        }
        return renderer;
    }

    getCorrectRendererGivenGeometryType(geometryType: string, renderers: any[]) {
        const symbolType = this.getSymbolTypeFromGeometryType(geometryType);
        const rendererType = renderers[0].type;
        let filteredRenderers = [];
        switch (rendererType) {
            case 'class-breaks':
            case 'unique-value':
                let index = 0;
                if (renderers.length > 1 && geometryType === 'line') {
                    index = 1;
                }
                return renderers[index];
                break;
            case 'simple':
            default:
                filteredRenderers = renderers.filter(r => r.symbol && r.symbol.type === symbolType);
                break;
        }

        if (filteredRenderers.length > 0) {
            return filteredRenderers[0];
        } else {
            //If not simple renderers, assume point first.
            let index = 0;
            if (geometryType !== 'point') {
                index = 1;
            }
            return renderers[index];
        }
        return null;
    }

    convertSymbolJSONToSymbolDefinition(symbol: any) {
        const captialWords = /([A-Z]{1,}[a-z]*)/g;
        switch (symbol.type) {
            case 'esriSMS':
                symbol.type = 'simple-marker';
                symbol.style = symbol.style.replace('esriSMS', '').match(captialWords).join('-').toLowerCase();
                symbol.outline = this.convertSymbolJSONToSymbolDefinition(symbol.outline);
                break;
            case 'esriPMS':
                symbol.type = 'picture-marker';
                break;
            case 'esriSLS':
                symbol.type = 'simple-line';
                symbol.style = symbol.style.replace('esriSLS', '').match(captialWords).join('-').toLowerCase();
                break;
            case 'esriSFS':
                symbol.type = 'simple-fill';
                symbol.style = symbol.style.replace('esriSFS', '').match(captialWords).join('-').toLowerCase();
                symbol.outline = this.convertSymbolJSONToSymbolDefinition(symbol.outline);
                break;
            case 'esriTS':
                symbol.type = 'text';
                break;
            default:
                break;
        }

        if (symbol.style === 'null') {
            symbol.style = 'none';
        }

        this.convertSymbolAlphaColorToZeroToOneBased(symbol);
        return symbol;
    }

    getSymbolDefinitionFromGeometry(geometryType: string, color: number[], outlineColor: number[] = null) {
        const geometry = geometryType.toLowerCase();
        const size = 12;
        const borderWidth = 1;
        const lineWidth = 3;
        const outlineWidth = 1;
        switch (geometry) {
            case 'point':
            case 'multipoint':
                return this.getSimpleMarkerDefinition(color, size, color, borderWidth);
            case 'line':
            case 'polyline':
                return this.getSimpleLineDefinition(color, lineWidth);
            case 'polygon':
                if (!outlineColor) {
                    outlineColor = color;
                }
                return this.getSimpleFillDefinition(color, outlineColor, outlineWidth);
            default:
                break;
        }
    }

    getProperSymbolDefinitonBasedOnGeometryType(geometryType: string, symbols: __esri.Symbol[]) {
        const type = this.getSymbolTypeFromGeometryType(geometryType);
        const symbol = symbols.find(s => s.type === type);
        return this.convertSymbolAlphaColorToZeroToOneBased(symbol);
    }

    public getExistingLayerColors(map: __esri.Map): number[][] {
        const layers = map.allLayers.toArray() as MapCoreLayer[];
        let renderers: __esri.Renderer[] = [];
        for (const layer of layers) {
            if (this.isMapImageLayer(layer)) {
                for (const sublayer of layer.allSublayers.toArray()) {
                    renderers.push(sublayer.renderer);
                }
            }
            if (this.isFeatureLayer(layer)) {
                renderers.push(layer.renderer);
            }
        }
        renderers = renderers.filter(x => x);
        const existingColors: number[][] = [];
        for (const renderer of renderers) {
            if (this.isSimpleRenderer(renderer) && renderer.symbol && renderer.symbol.color) {
                const color = renderer.symbol.color;
                existingColors.push([color.r, color.g, color.b]);
            }
        }
        return existingColors;
    }

    convertSymbolAlphaColorToZeroToOneBased(symbol: __esri.Symbol) {
        const alphaIndex = 3;
        const colorLengthWithA = 4;
        if (symbol.color && Array.isArray(symbol.color) && symbol.color.length === colorLengthWithA && symbol.color[alphaIndex] > 1) {
            const maxByteValue = 255;
            const hundred = 100;
            let value = symbol.color[alphaIndex] / maxByteValue;
            value = Math.round(value * hundred) / hundred;
            symbol.color[alphaIndex] = value;
        }
        return symbol;
    }

    getSymbolTypeFromGeometryType(geometryType: string) {
        let type: string = null;
        switch (geometryType.toLowerCase()) {
            case 'point':
            case 'multipoint':
                type = 'simple-marker';
                break;
            case 'line':
            case 'polyline':
                type = 'simple-line';
                break;
            case 'polygon':
                type = 'simple-fill';
                break;
            default:
                break;
        }
        return type;
    }

    getSimpleLineDefinition(color, width) {
        return {
            color,
            style: 'solid',
            type: 'simple-line',
            width
        } as __esri.SimpleLineSymbolProperties;
    }

    getSimpleMarkerDefinition(color, size, borderColor, borderWidth) {
        return {
            color,
            type: 'simple-marker',
            size,
            outline: this.getSimpleLineDefinition(borderColor, borderWidth)
        } as __esri.SimpleMarkerSymbolProperties;
    }

    getSimpleFillDefinition(fillColor, borderColor, borderWidth) {
        return {
            color: fillColor,
            type: 'simple-fill',
            style: 'none',
            outline: this.getSimpleLineDefinition(borderColor, borderWidth)
        } as __esri.SimpleFillSymbolProperties;
    }

    private getCorsEnabledHosts(): any[] {
        // TODO: hardcode for now
        return [
            { host: 'www.dotdev7.state.pa.us/penndotgis', withCredentials: true }
        ];
    }

    private isMapImageLayer(layer: MapCoreLayer): layer is IMapcoreMapImageLayer {
        return layer.type === 'map-image';
    }

    private isFeatureLayer(layer: MapCoreLayer): layer is IMapcoreFeatureLayer {
        return layer.type === 'feature';
    }

    private isSimpleRenderer(renderer: __esri.Renderer): renderer is __esri.SimpleRenderer {
        return renderer.type === 'simple';
    }
}
